// src/components/OfferPage/OfferPage.js

import React, { useState, useEffect, useMemo } from 'react';
import Navbar from '../Navbar/Navbar';
import api from '../Axios/axios';
import './OfferPage.scss';

const STAR_CRANE_COMPANY_ID = 'b6af2c6e-168e-447f-8b85-08ddb5506453';
const DEFAULT_SERVICE_SUGGESTIONS = [
  'Montaj',
  'Periyodik Bakım',
  'De Montaj',
  'Ankraj Ayağı',
  'Ankraj montajı'
];

const DEFAULT_RENTAL_DESCRIPTION_LINES = [
  'Vinç, expertizi yapılıp şantiyeye gelecek.',
  'Rezerve edilmemiş vinçler başka yere kiralanabilir.',
  'İlk kira ve işçilik ücretleri peşin - havale olarak ödenir.',
  'Makineye yetkili veya yetkilendirilmiş servis dışında müdahale edilmesi yasaktır.',
  'Teklif geçerlilik süresi 7 gündür.',
  'Demontaj işlemleri iş bitiminde güncel koşullara göre faturalandırılır.',
  'Projeye intikal edince makine test edilecek.',
  'İşe verme kontrol formu MMO veya muadili kurum tarafından düzenlenir',
  '8 Tır ile nakliye edilir.',
  'Kurulumu 160-180 ton mobil vinç ile yapılır',
  'Makine sigortası "All Risk"e dahil edilir',
  'Geliş ve dönüş (İstanbul) nakliyesi projeye aittir. (Şantiyeden kiralama yapılırsa yeni kiracı öder)',
  'Kaldırma zinciri projeye aittir.',
  'Teknik servis yol yemek ve konaklama (köprü otoyol) projeye aittir.'
];

const mergeDescriptionLines = (lines = []) => {
  const seen = new Set();
  return [...DEFAULT_RENTAL_DESCRIPTION_LINES, ...lines]
    .map(line => String(line || '').trim())
    .filter(line => {
      if (!line || line.toUpperCase() === 'DENSE') return false;
      const key = line.toLocaleLowerCase('tr-TR');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const createServiceSuggestion = (label, overrides = {}) => ({
  label,
  price: overrides.price ?? '',
  qty: overrides.qty ?? 1
});

const createEmptyServiceItem = () => ({
  id: Date.now(),
  label: '',
  price: '',
  qty: 1
});

export default function OfferPage() {
  const kiralayan = STAR_CRANE_COMPANY_ID;
  const [isSavingOffer, setIsSavingOffer] = useState(false);
  const [notification, setNotification] = useState(null);
  const [ownerDetails, setOwnerDetails] = useState({
    companyName: '',
    taxNo: '',
    address: '',
    contact: '',
    phone: '',
    email: ''
  });

  const [kiraciDetails, setKiraciDetails] = useState({
    companyName: '',
    taxOffice: '',
    taxNo: '',
    address: '',
    contact: '',
    phone: '',
    email: ''
  });

  const [siteInput, setSiteInput] = useState('');

  const [machinesAll, setMachinesAll] = useState([]);
  const [uniqueMachines, setUniqueMachines] = useState([]);
  const [machine, setMachine] = useState('');
  const [machineDetails, setMachineDetails] = useState({
    serial: '',
    brand: '',
    model: '',
    maxTonnage: '',
    height: '',
    jib: '',
    chassis: '',
    walking: ''
  });

  const [details, setDetails] = useState(DEFAULT_RENTAL_DESCRIPTION_LINES);
  const [defaultDetails, setDefaultDetails] = useState(DEFAULT_RENTAL_DESCRIPTION_LINES);
  const [suggestedServiceItems, setSuggestedServiceItems] = useState(
    DEFAULT_SERVICE_SUGGESTIONS.map(label => createServiceSuggestion(label))
  );
  const [rental, setRental] = useState({ start: '', months: 6, price: '', serviceItems: [] });
  const [plan, setPlan] = useState([]);
  const getMachineLabel = (m) =>
    `${m.serialNo ? `${m.serialNo} - ` : ''}${m.brand || ''} ${m.model || ''} ${m.maxTonnage || ''} ton`.trim();

  const selectedMachine = useMemo(
    () => machinesAll.find(m => String(m.id) === String(machine)) || null,
    [machine, machinesAll]
  );

  // Default service items
  useEffect(() => {
    api.get('/DefaultService')
      .then(res => {
        if (res.data && Array.isArray(res.data.data)) {
          const serviceMap = new Map(
            res.data.data.map(s => [String(s.name || '').trim().toLocaleLowerCase('tr-TR'), s])
          );
          setSuggestedServiceItems(
            DEFAULT_SERVICE_SUGGESTIONS.map(label => {
              const source = serviceMap.get(label.toLocaleLowerCase('tr-TR'));
              return createServiceSuggestion(label, {
                price: source?.price ?? '',
                qty: source?.piece ?? 1
              });
            })
          );
        }
      })
      .catch(() => console.error('Default services alınamadı.'));
  }, []);

  // Fetch machines, descriptions
  useEffect(() => {
    api.get('/TowerCrane').then(res => {
      setMachinesAll(res.data.data);
      const seen = new Set();
      const unique = res.data.data.filter(m => {
        const key = String(m.id);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setUniqueMachines(unique);
    });
    api.get('/Description', { params: { type: 'RentalOffer' } })
      .then(res => {
        if (res.data && Array.isArray(res.data.data)) {
          const cleanedDetails = res.data.data
            .map(d => d.name || d.description)
            .filter(d => String(d || '').trim().toUpperCase() !== 'DENSE');
          const mergedDetails = mergeDescriptionLines(cleanedDetails);
          setDetails(mergedDetails);
          setDefaultDetails(mergedDetails);
        }
      })
      .catch(() => {
        setDetails(DEFAULT_RENTAL_DESCRIPTION_LINES);
        setDefaultDetails(DEFAULT_RENTAL_DESCRIPTION_LINES);
      });
  }, []);

  // When kiralayan changes
  useEffect(() => {
    if (!kiralayan) {
      setOwnerDetails({ companyName: '', taxNo: '', address: '', contact: '', phone: '', email: '' });
      return;
    }
    api.get(`/Company/GetById?id=${kiralayan}`).then(res => {
      const data = res.data.data;
      setOwnerDetails({
        companyName: data.name || 'STAR KULE VİNÇ VE İŞ MAKİNELERİ KİRALAMA ANONİM ŞİRKETİ',
        taxNo: data.taxNo || '',
        address: data.address || '',
        contact: data.authorizedPerson1 || '',
        phone: data.phoneNumber1 || '',
        email: data.email || ''
      });
    });
  }, [kiralayan]);

  // When machine changes
  useEffect(() => {
    if (!machine) {
      setMachineDetails({
        serial: '',
        brand: '',
        model: '',
        maxTonnage: '',
        height: '',
        jib: '',
        chassis: '',
        walking: ''
      });
      return;
    }
    const sel = machinesAll.find(m => String(m.id) === String(machine));
    if (sel) {
      setMachineDetails({
        serial: sel.serialNo || '',
        brand: sel.brand || '',
        model: sel.model || '',
        maxTonnage: sel.maxTonnage || '',
        height: sel.height || '',
        jib: sel.jibLength || sel.bom || sel.jib || '',
        chassis: sel.type || '',
        walking: sel.hasWalkingGear == null ? '' : sel.hasWalkingGear
      });
    }
  }, [machine, machinesAll]);

  // Compute end date
  const endDate = useMemo(() => {
    if (!rental.start) return '';
    const d = new Date(rental.start);
    d.setMonth(d.getMonth() + rental.months);
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  }, [rental.start, rental.months]);

  // Compute totals
  const cleanRent = useMemo(() =>
    parseFloat(String(rental.price).replace(/\./g, '').replace(/,/g, '.')) || 0
  , [rental.price]);

  const monthlyRentItem = useMemo(() => ({
    label: 'Aylık Kira Bedeli',
    price: cleanRent,
    qty: rental.months,
    total: cleanRent * rental.months * 1.2
  }), [cleanRent, rental.months]);

  const total = useMemo(() =>
    rental.serviceItems.reduce((sum, i) => sum + Number(i.price || 0) * i.qty, 0)
    + (monthlyRentItem.price * monthlyRentItem.qty)
  , [monthlyRentItem, rental.serviceItems]);

  const vatTotal = useMemo(() => total * 0.20, [total]);
  const totalWithVAT = useMemo(() => total + vatTotal, [total, vatTotal]);

  // Build payment plan
  useEffect(() => {
    if (!rental.start) return;
    const arr = [];
    const kiraAylikKDVli = cleanRent * 1.2;
    const hizmetToplamKDVli = rental.serviceItems.reduce(
      (sum, i) => sum + Number(i.price || 0) * i.qty * 1.2, 0
    );
    for (let i = 0; i < rental.months; i++) {
      const dt = new Date(rental.start);
      dt.setMonth(dt.getMonth() + i);
      arr.push({
        no: i + 1,
        date: dt.toISOString().slice(0, 10),
        method: i === 0 ? 'Havale' : 'Çek',
        amount: i === 0
          ? kiraAylikKDVli + hizmetToplamKDVli
          : kiraAylikKDVli
      });
    }
    setPlan(arr);
  }, [rental.start, rental.months, cleanRent, rental.serviceItems]);

  const planTotal = useMemo(() =>
    plan.reduce((s, p) => s + p.amount, 0), [plan]);

  const onAmountChange = (idx, v) => {
    const val = Number(v) || 0;
    setPlan(prev => {
      const newArr = prev.map((x, i) => i === idx ? { ...x, amount: val } : x);
      const sumFixed = newArr.slice(0, idx + 1).reduce((s, x) => s + x.amount, 0);
      const rem = totalWithVAT - sumFixed;
      const count = newArr.length - (idx + 1);
      const dist = count > 0 ? rem / count : 0;
      for (let i = idx + 1; i < newArr.length; i++) newArr[i].amount = dist;
      return newArr;
    });
  };

  const mapPaymentType = m => {
    if (m === 'Çek') return 'Cheque';
    if (m === 'Senet') return 'Bond';
    return 'Transfer';
  };

  const showNotification = (type, title, message) => {
    setNotification({ type, title, message });
  };

  const resetOfferForm = () => {
    setKiraciDetails({
      companyName: '',
      taxOffice: '',
      taxNo: '',
      address: '',
      contact: '',
      phone: '',
      email: ''
    });
    setSiteInput('');
    setMachine('');
    setMachineDetails({
      serial: '',
      brand: '',
      model: '',
      maxTonnage: '',
      height: '',
      jib: '',
      chassis: '',
      walking: ''
    });
    setRental({ start: '', months: 6, price: '', serviceItems: [] });
    setPlan([]);
    setDetails(defaultDetails);
  };

  const addSuggestedServiceItem = (suggestion) => {
    const alreadyAdded = rental.serviceItems.some(
      item => String(item.label || '').trim().toLocaleLowerCase('tr-TR') ===
        String(suggestion.label || '').trim().toLocaleLowerCase('tr-TR')
    );

    if (alreadyAdded) {
      showNotification('info', 'Hizmet zaten eklendi', `${suggestion.label} kalemi listede var.`);
      return;
    }

    setRental(r => ({
      ...r,
      serviceItems: [
        ...r.serviceItems,
        {
          id: `${Date.now()}-${suggestion.label}`,
          label: suggestion.label,
          price: suggestion.price || '',
          qty: suggestion.qty || 1
        }
      ]
    }));
  };

  const getServiceKey = (label) => String(label || '').trim().toLocaleLowerCase('tr-TR');

  const findServiceItemIndex = (label) =>
    rental.serviceItems.findIndex(item => getServiceKey(item.label) === getServiceKey(label));

  const updateServiceItem = (idx, patch) => {
    setRental(r => {
      const arr = [...r.serviceItems];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...r, serviceItems: arr };
    });
  };

  // Save
  const saveOffer = () => {
    if (isSavingOffer) return;

    if (!machine) {
      showNotification('error', 'Eksik bilgi', 'Makine seçilmedi.');
      return;
    }

    if (!String(kiraciDetails.companyName || '').trim()) {
      showNotification('error', 'Eksik bilgi', 'Kiracı firma adını girin.');
      return;
    }

    if (!String(rental.start || '').trim()) {
      showNotification('error', 'Eksik bilgi', 'Başlangıç tarihi seçin.');
      return;
    }

    if (!Number(rental.months || 0) || Number(rental.months || 0) < 1) {
      showNotification('error', 'Eksik bilgi', 'Kira süresi en az 1 ay olmalı.');
      return;
    }

    if (!cleanRent || cleanRent <= 0) {
      showNotification('error', 'Eksik bilgi', 'Aylık kira bedelini girin.');
      return;
    }

    const machineLabel = selectedMachine ? getMachineLabel(selectedMachine) : machine;
    const payload = {
      rentingCompanyId: kiralayan,
      tenantCompany: kiraciDetails.companyName,
      tenantCompanyTaxOffice: kiraciDetails.taxOffice,
      tenantCompanyTaxNo: kiraciDetails.taxNo,
      tenantCompanyName: kiraciDetails.companyName,
      tenantCompanyAddress: kiraciDetails.address,
      tenantCompanyAuthorizedPerson: kiraciDetails.contact,
      tenantCompanyAuthorizedPersonPhone: kiraciDetails.phone,
      tenantCompanyAuthorizedPersonEmail: kiraciDetails.email,
      buildingSite: siteInput,
      machine: machineLabel,
      model: machineDetails.model,
      serialNo: machineDetails.serial,
      type: machineDetails.chassis,
      installedHeight: parseFloat(machineDetails.height) || 0,
      jibLength: parseFloat(machineDetails.jib) || 0,
      hasWalkingGear: machineDetails.walking === true,
      startDate: rental.start || null,
      endDate: endDate || null,
      rentalPeriod: rental.months,
      rent: cleanRent,
      total,
      vatTotal,
      totalWithVAT,
      services: [
        { name: monthlyRentItem.label, price: monthlyRentItem.price, piece: monthlyRentItem.qty },
        ...rental.serviceItems
          .filter(s => String(s.label || '').trim())
          .map(s => ({
          name: s.label,
          price: parseFloat(s.price) || 0,
          piece: Number(s.qty || 1)
        }))
      ],
      payments: plan.map(p => ({
        no: p.no,
        paymentType: mapPaymentType(p.method),
        month: new Date(p.date).getMonth() + 1,
        year: new Date(p.date).getFullYear(),
        price: p.amount
      })),
      descriptions: details
        .map(d => String(d || '').trim())
        .filter(Boolean)
        .map(d => ({ description: d }))
    };

    setIsSavingOffer(true);
    api.post('/RentalOffer', payload)
      .then(() => {
        showNotification('success', 'Teklif kaydedildi', 'Kiralık teklif başarıyla kaydedildi.');
        resetOfferForm();
      })
      .catch((error) => {
        const validationErrors = error?.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join(' ')
          : '';
        const backendMessage =
          (typeof error?.response?.data === 'string' ? error.response.data : '') ||
          validationErrors ||
          error?.response?.data?.messages?.join(' ') ||
          error?.response?.data?.message ||
          error?.response?.data?.title ||
          'Kayıt sırasında hata oluştu.';

        showNotification('error', 'Kayıt başarısız', backendMessage);
      })
      .finally(() => setIsSavingOffer(false));
  };

  return (
    <>
      <Navbar />
      <div className="offer-page">
        <h2>Kira Teklifi</h2>
        <div className="offer-form">

          {/* Kiralayan Firma */}
          <div className="form-row">
            <label>Kiralayan Firma</label>
            <input
              value={ownerDetails.companyName}
              onChange={e => setOwnerDetails({ ...ownerDetails, companyName: e.target.value })}
            />
          </div>

          {/* Kiralayan Detayları */}
          <div className="form-row multi">
            <div>
              <label>Vergi No</label>
              <input
                value={ownerDetails.taxNo}
                onChange={e => setOwnerDetails({ ...ownerDetails, taxNo: e.target.value })}
              />
            </div>
            <div>
              <label>Adres</label>
              <input
                value={ownerDetails.address}
                onChange={e => setOwnerDetails({ ...ownerDetails, address: e.target.value })}
              />
            </div>
            <div>
              <label>Yetkili</label>
              <input
                value={ownerDetails.contact}
                onChange={e => setOwnerDetails({ ...ownerDetails, contact: e.target.value })}
              />
            </div>
            <div>
              <label>Tel</label>
              <input
                value={ownerDetails.phone}
                onChange={e => setOwnerDetails({ ...ownerDetails, phone: e.target.value })}
              />
            </div>
            <div>
              <label>Email</label>
              <input
                value={ownerDetails.email}
                onChange={e => setOwnerDetails({ ...ownerDetails, email: e.target.value })}
              />
            </div>
          </div>

          {/* Kiracı Firma */}
          <div className="form-row manual-company-row">
            <label>Kiracı Firma</label>
            <input
              type="text"
              placeholder="Firma adı girin"
              value={kiraciDetails.companyName}
              onChange={e => setKiraciDetails({ ...kiraciDetails, companyName: e.target.value })}
            />
          </div>

          {/* Kiracı Detayları */}
          <div className="form-row multi">
            <div>
              <label>Vergi Dairesi</label>
              <input
                value={kiraciDetails.taxOffice}
                onChange={e => setKiraciDetails({ ...kiraciDetails, taxOffice: e.target.value })}
              />
            </div>
            <div>
              <label>Vergi No</label>
              <input
                value={kiraciDetails.taxNo}
                onChange={e => setKiraciDetails({ ...kiraciDetails, taxNo: e.target.value })}
              />
            </div>
            <div>
              <label>Adres</label>
              <input
                value={kiraciDetails.address}
                onChange={e => setKiraciDetails({ ...kiraciDetails, address: e.target.value })}
              />
            </div>
            <div>
              <label>Yetkili</label>
              <input
                value={kiraciDetails.contact}
                onChange={e => setKiraciDetails({ ...kiraciDetails, contact: e.target.value })}
              />
            </div>
            <div>
              <label>Tel</label>
              <input
                value={kiraciDetails.phone}
                onChange={e => setKiraciDetails({ ...kiraciDetails, phone: e.target.value })}
              />
            </div>
            <div>
              <label>Email</label>
              <input
                value={kiraciDetails.email}
                onChange={e => setKiraciDetails({ ...kiraciDetails, email: e.target.value })}
              />
            </div>
          </div>

          {/* Şantiye & Makine */}
          <div className="form-row multi">
            <div>
              <label>Şantiye (Seç veya Yaz)</label>
              <input
                placeholder="Manuel yazın"
                value={siteInput}
                onChange={e => setSiteInput(e.target.value)}
              />
            </div>
            <div>
              <label>Makine</label>
              <select
                value={machine}
                onChange={e => setMachine(e.target.value)}
                required
              >
                <option value="" disabled>Makine Seçin...</option>
                {uniqueMachines.map(m => (
                  <option key={m.id} value={m.id}>{getMachineLabel(m)}</option>
                ))}
              </select>
            </div>
            <div>
              <label>Seri No</label>
              <input
                value={machineDetails.serial}
                onChange={e => setMachineDetails({ ...machineDetails, serial: e.target.value })}
              />
            </div>
            <div>
              <label>Model</label>
              <input
                value={machineDetails.model}
                onChange={e => setMachineDetails({ ...machineDetails, model: e.target.value })}
              />
            </div>
            <div>
              <label>Yükseklik (m)</label>
              <input
                type="number"
                value={machineDetails.height}
                onChange={e => setMachineDetails({ ...machineDetails, height: e.target.value })}
              />
            </div>
            <div>
              <label>Bom (m)</label>
              <input
                type="number"
                value={machineDetails.jib}
                onChange={e => setMachineDetails({ ...machineDetails, jib: e.target.value })}
              />
            </div>
            <div>
              <label>Şase Tipi</label>
              <select
                value={machineDetails.chassis}
                onChange={e => setMachineDetails({ ...machineDetails, chassis: e.target.value })}
              >
                <option value="">Seçin...</option>
                <option value="Chassis">Şaseli</option>
                <option value="Anchored">Statik</option>
              </select>
            </div>
            <div>
              <label>Yürüyüş Donanımı</label>
              <select
                value={String(machineDetails.walking)}
                onChange={e => setMachineDetails({ ...machineDetails, walking: e.target.value === 'true' })}
              >
                <option value="true">Var</option>
                <option value="false">Yok</option>
              </select>
            </div>
          </div>

          {/* Kira & Tarihler */}
          <div className="form-row multi">
            <div>
              <label>Başlangıç</label>
              <input
                type="date"
                value={rental.start}
                onChange={e => setRental(r => ({ ...r, start: e.target.value }))}
              />
            </div>
            <div>
              <label>Ay</label>
              <input
                type="number"
                min="1"
                value={rental.months}
                onChange={e => setRental(r => ({ ...r, months: Number(e.target.value) }))}
              />
            </div>
            <div>
              <label>Bitiş</label>
              <input type="date" readOnly value={endDate} />
            </div>
            <div>
              <label>Aylık Kira (₺)</label>
              <input
                value={rental.price}
                onChange={e => setRental(r => ({ ...r, price: e.target.value }))}
              />
            </div>
          </div>

          {/* Servis & İşçilik */}
          <h3>Servis & İşçilik</h3>
          <table className="service-table">
            <thead>
              <tr>
                <th>Hizmet</th>
                <th>Birim Fiyat</th>
                <th>Adet / Ay</th>
                <th>Tutar (KDV %20)</th>
                <th>Sil</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{monthlyRentItem.label}</td>
                <td>{monthlyRentItem.price.toLocaleString()} ₺</td>
                <td>{monthlyRentItem.qty}</td>
                <td>{monthlyRentItem.total.toLocaleString()} ₺</td>
                <td>-</td>
              </tr>
              {suggestedServiceItems.map((suggestion) => {
                const idx = findServiceItemIndex(suggestion.label);
                const sv = idx >= 0 ? rental.serviceItems[idx] : null;
                const totalRow = sv ? Number(sv.price || 0) * sv.qty * 1.2 : 0;

                if (!sv) {
                  return (
                    <tr key={suggestion.label} className="service-placeholder-row">
                      <td>{suggestion.label}</td>
                      <td>{suggestion.price ? `${Number(suggestion.price || 0).toLocaleString()} ₺` : '-'}</td>
                      <td>{suggestion.qty || 1}</td>
                      <td>-</td>
                      <td>
                        <button
                          type="button"
                          className="add-service-row-btn"
                          onClick={() => addSuggestedServiceItem(suggestion)}
                        >
                          Ekle
                        </button>
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr key={suggestion.label}>
                    <td>
                      <input
                        value={sv.label}
                        onChange={e => updateServiceItem(idx, { label: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={sv.price}
                        onChange={e => updateServiceItem(idx, { price: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="1"
                        value={sv.qty}
                        onChange={e => updateServiceItem(idx, { qty: Number(e.target.value) })}
                      />
                    </td>
                    <td>{totalRow.toLocaleString()} ₺</td>
                    <td>
                      <button className="remove-btn" onClick={() => {
                        setRental(r => ({
                          ...r,
                          serviceItems: r.serviceItems.filter((_, i) => i !== idx)
                        }));
                      }}>-</button>
                    </td>
                  </tr>
                );
              })}
              {rental.serviceItems
                .map((sv, idx) => ({ sv, idx }))
                .filter(({ sv }) => !suggestedServiceItems.some(
                  suggestion => getServiceKey(suggestion.label) === getServiceKey(sv.label)
                ))
                .map(({ sv, idx }) => {
                const totalRow = Number(sv.price || 0) * sv.qty * 1.2;
                return (
                  <tr key={sv.id || idx}>
                    <td>
                      <input
                        value={sv.label}
                        onChange={e => {
                          const lbl = e.target.value;
                          setRental(r => {
                            const arr = [...r.serviceItems];
                            arr[idx].label = lbl;
                            return { ...r, serviceItems: arr };
                          });
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={sv.price}
                        onChange={e => {
                          const pr = e.target.value;
                          setRental(r => {
                            const arr = [...r.serviceItems];
                            arr[idx].price = pr;
                            return { ...r, serviceItems: arr };
                          });
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="1"
                        value={sv.qty}
                        onChange={e => {
                          const q = Number(e.target.value);
                          setRental(r => {
                            const arr = [...r.serviceItems];
                            arr[idx].qty = q;
                            return { ...r, serviceItems: arr };
                          });
                        }}
                      />
                    </td>
                    <td>{totalRow.toLocaleString()} ₺</td>
                    <td>
                      <button className="remove-btn" onClick={() => {
                        setRental(r => ({
                          ...r,
                          serviceItems: r.serviceItems.filter((_, i) => i !== idx)
                        }));
                      }}>-</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="4">Toplam</td>
                <td>{totalWithVAT.toLocaleString()} ₺</td>
              </tr>
            </tfoot>
          </table>
          <button className="btn-small" onClick={() => {
            setRental(r => ({
              ...r,
              serviceItems: [...r.serviceItems, createEmptyServiceItem()]
            }));
          }}>+ Hizmet Ekle</button>

          {/* Toplamlar */}
          <div className="totals">
            <div>KDV Hariç Toplam: {total.toLocaleString()} ₺</div>
            <div>KDV (%20): {vatTotal.toLocaleString()} ₺</div>
            <div>KDV Dahil Toplam: {totalWithVAT.toLocaleString()} ₺</div>
          </div>

          {/* Ödeme Planı */}
          <h3>Ödeme Planı</h3>
          <div className="plan-wrapper">
            <table className="plan">
              <thead>
                <tr>
                  <th>No</th>
                  <th>Ay</th>
                  <th>Şekil</th>
                  <th>Tutar</th>
                  <th>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {plan.map((p, i) => (
                  <tr key={i}>
                    <td>{p.no}</td>
                    <td>
                      <input
                        value={p.date}
                        onChange={e => {
                          const d = e.target.value;
                          setPlan(arr => arr.map((x, j) => j === i ? { ...x, date: d } : x));
                        }}
                      />
                    </td>
                    <td>
                      <select
                        value={p.method}
                        onChange={e => {
                          const m = e.target.value;
                          setPlan(arr => arr.map((x, j) => j === i ? { ...x, method: m } : x));
                        }}
                      >
                        <option>Havale</option>
                        <option>Çek</option>
                        <option>Senet</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        value={p.amount}
                        onChange={e => onAmountChange(i, e.target.value)}
                      />
                    </td>
                    <td>
                      <button className="remove-btn" onClick={() => {
                        setPlan(arr => arr.filter((_, j) => j !== i));
                      }}>-</button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="4" className="add-plan" onClick={() => {
                    setPlan(p => [...p, { no: p.length+1, date:'', method:'Çek', amount:0 }]);
                  }}>+ Ödeme Ekle</td>
                  <td>{planTotal.toLocaleString()} ₺</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Diğer Bilgiler - Açıklama */}
          <h3>Diğer Bilgiler - Açıklama</h3>
          <div className="details-list">
          {details.map((d, i) => (
            <div className="details-container" key={i}>
              <input
                className="detail-input"
                value={d}
                placeholder="Açıklama satırı yazın"
                onChange={e => {
                  const v = e.target.value;
                  setDetails(arr => {
                    const c = [...arr];
                    c[i] = v;
                    return c;
                  });
                }}
              />
              <button
                type="button"
                className="detail-remove-btn"
                onClick={() => setDetails(arr => arr.filter((_, index) => index !== i))}
              >
                Kaldır
              </button>
            </div>
          ))}
          </div>
          <button className="btn-small" onClick={() => {
            setDetails(d => [...d, '']);
          }}>+ Detay Ekle</button>

          <button className="btn-primary" onClick={saveOffer} disabled={isSavingOffer}>
            {isSavingOffer ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>

      {notification && (
        <div className="offer-notification-backdrop" role="presentation">
          <div className={`offer-notification-modal ${notification.type}`} role="alertdialog">
            <h3>{notification.title}</h3>
            <p>{notification.message}</p>
            <button type="button" onClick={() => setNotification(null)}>
              Tamam
            </button>
          </div>
        </div>
      )}
    </>
  );
}
